import { html } from "htm/preact";
import { useEffect } from "preact/hooks";

export default function FeatureFilters() {
  // Get all cards with data-active attribute (all filterable items)
  const getCards = () => {
    return Array.from(document.querySelectorAll("[data-active]"));
  };

  // Get all select elements
  const getSelects = () => {
    return Array.from(document.querySelectorAll("select[data-filter-key]"));
  };

  // Get the reset button
  const getResetButton = () => {
    return document.getElementById("filters-button-reset");
  };

  // Get current filter values from the DOM
  const getCurrentFilters = () => {
    const filters = {};
    getSelects().forEach((select) => {
      const filterKey = select.getAttribute("data-filter-key");
      filters[filterKey] = select.value;
    });
    return filters;
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    const currentFilters = getCurrentFilters();
    return Object.values(currentFilters).some((value) => value !== "");
  };

  // Update reset button state
  const updateResetButtonState = () => {
    const resetButton = getResetButton();
    if (resetButton) {
      if (hasActiveFilters()) {
        resetButton.disabled = false;
      } else {
        resetButton.disabled = true;
      }
    }
  };

  // Reset all filters
  const resetFilters = () => {
    getSelects().forEach((select) => {
      select.value = "";
    });
    filterCards();
    updateAvailableOptions();
    updateResetButtonState();
  };

  // Filter cards based on current filter values
  const filterCards = () => {
    const currentFilters = getCurrentFilters();
    const cards = getCards();
    const selects = getSelects();
    let visibleCount = 0;

    // Build a map of filter keys to card attribute names
    const filterToCardAttr = {};
    selects.forEach((select) => {
      const filterKey = select.getAttribute("data-filter-key");
      const cardAttr = select.getAttribute("data-card-attribute");
      filterToCardAttr[filterKey] = cardAttr;
    });

    cards.forEach((card) => {
      const matchesAllFilters = Object.entries(currentFilters).every(
        ([key, value]) => {
          if (!value) return true; // If no filter is set, it matches

          const cardAttrName = filterToCardAttr[key];
          const dataKey = `data-${cardAttrName}`;
          const cardValue = card.getAttribute(dataKey);

          return cardValue === value;
        },
      );

      if (matchesAllFilters) {
        card.setAttribute("data-active", "true");
        visibleCount++;
      } else {
        card.setAttribute("data-active", "false");
      }
    });

    // Update the items count
    const countElement = document.querySelector("[data-items-quantity]");
    if (countElement) {
      countElement.setAttribute("data-items-quantity", visibleCount.toString());
      countElement.textContent = visibleCount.toString();
    }

    // Update the singular/plural text
    const textElement = document.querySelector(
      "[data-items-text-products-singular]",
    );
    if (textElement) {
      const singularText = textElement.getAttribute(
        "data-items-text-products-singular",
      );
      const pluralText = textElement.getAttribute(
        "data-items-text-products-plural",
      );
      textElement.textContent = visibleCount === 1 ? singularText : pluralText;
    }

    // Briefly set data-updating to signal the value has changed
    const countDisplay = document.getElementById("count-products-display");
    if (countDisplay) {
      countDisplay.setAttribute("data-updating", "");
      setTimeout(() => {
        countDisplay.removeAttribute("data-updating");
      }, 150);
    }
  };

  // Update available options in all selects based on visible cards
  const updateAvailableOptions = () => {
    const currentFilters = getCurrentFilters();
    const cards = getCards();
    const selects = getSelects();

    // Build a map of filter keys to card attribute names
    const filterToCardAttr = {};
    selects.forEach((s) => {
      const filterKey = s.getAttribute("data-filter-key");
      const cardAttr = s.getAttribute("data-card-attribute");
      filterToCardAttr[filterKey] = cardAttr;
    });

    selects.forEach((select) => {
      const filterKey = select.getAttribute("data-filter-key");
      const cardAttrName = filterToCardAttr[filterKey];
      const dataKey = `data-${cardAttrName}`;
      const currentValue = select.value;

      // Get all options except "All"
      const options = Array.from(select.options).slice(1);

      // Find which values are available in cards that match other filters
      const availableValues = new Set();

      cards.forEach((card) => {
        // Check if card matches all OTHER filters (not this one)
        const matchesOtherFilters = Object.entries(currentFilters).every(
          ([key, value]) => {
            if (key === filterKey) return true; // Skip the current filter
            if (!value) return true; // If no filter is set, it matches

            const otherCardAttrName = filterToCardAttr[key];
            const otherDataKey = `data-${otherCardAttrName}`;
            const cardValue = card.getAttribute(otherDataKey);

            return cardValue === value;
          },
        );

        if (matchesOtherFilters) {
          const cardValue = card.getAttribute(dataKey);
          if (cardValue && cardValue !== "false") {
            availableValues.add(cardValue);
          }
        }
      });

      // Enable/disable options based on availability
      options.forEach((option) => {
        const optionValue = option.value;
        if (availableValues.has(optionValue)) {
          option.disabled = false;
        } else {
          option.disabled = true;
        }
      });
    });
  };

  // Handle filter change
  const handleFilterChange = () => {
    filterCards();
    updateAvailableOptions();
    updateResetButtonState();
  };

  useEffect(() => {
    const selects = getSelects();
    const resetButton = getResetButton();

    // Add event listeners to all selects
    selects.forEach((select) => {
      select.addEventListener("change", handleFilterChange);
    });

    // Add event listener to reset button
    if (resetButton) {
      resetButton.addEventListener("click", resetFilters);
    }

    // Initial update of available options
    updateAvailableOptions();

    // Cleanup
    return () => {
      selects.forEach((select) => {
        select.removeEventListener("change", handleFilterChange);
      });
      if (resetButton) {
        resetButton.removeEventListener("click", resetFilters);
      }
    };
  }, []);

  // Component doesn't render anything - it just manages the filtering logic
  return null;
}
